import * as React from 'react';
import { FlagLevel, getFlagLevelConfig } from '@app/model/Flag';

interface FlagLevelIconProps {
  level: FlagLevel;
  /** Extra inline styles, for the odd caller that has to nudge the icon into a line of text. */
  style?: React.CSSProperties;
  /**
   * An accessible name for the icon. Pass it where the icon alone carries the meaning; leave it
   * off where neighbouring text or a tooltip already says which level this is, so a screen reader
   * does not hear the level twice.
   */
  label?: string;
}

/**
 * The severity icon for a flag level. Every screen that shows a flag goes through here, so the
 * four levels keep the same glyph and colour wherever they turn up.
 */
export const FlagLevelIcon: React.FC<FlagLevelIconProps> = ({ level, style, label }) => {
  const config = getFlagLevelConfig(level);
  if (!config) {
    return null;
  }
  const Icon = config.icon;
  return label
    ? <Icon color={config.iconColor} style={style} role="img" aria-label={label} />
    : <Icon color={config.iconColor} style={style} aria-hidden="true" />;
};

/** The icon followed by the level's name, as the filter menu and its chips show it. */
export const FlagLevelText: React.FC<{ level: FlagLevel }> = ({ level }) => {
  const config = getFlagLevelConfig(level);
  if (!config) {
    return null;
  }
  return (
    <>
      <FlagLevelIcon level={level} />
      &nbsp;{config.text}
    </>
  );
};
